const config = require('../core/config');
const Lexer = require('../core/lexer');
const Parser = require('../core/parser');
const Interpreter = require('../core/interpreter');
const { Keywords } = require('../core/keywords');
const initKeyboard = require('./keyboard');
const Templates = require('./templates/index');
const SyntaxHighlighter = require('./highlighter');
const html2canvas = require('html2canvas');
require('simple-keyboard/build/css/index.css'); // Import CSS

document.addEventListener('DOMContentLoaded', () => {
    const codeEditor = document.getElementById('code-editor');
    const lineNumbers = document.getElementById('line-numbers');
    const highlightLayer = document.getElementById('highlight-layer');
    const outputConsole = document.getElementById('output-console');
    const languageSelect = document.getElementById('language-select');
    const runBtn = document.getElementById('run-btn');
    const layoutBtn = document.getElementById('layout-btn');
    const themeBtn = document.getElementById('theme-btn');
    const clearBtn = document.getElementById('clear-output');
    const workspace = document.querySelector('.editor-workspace');
    const saveModal = document.getElementById('save-modal');
    const closeSaveBtn = document.getElementById('close-save');
    const cancelSaveBtn = document.getElementById('cancel-save');
    const confirmSaveBtn = document.getElementById('confirm-save');
    const filenameInput = document.getElementById('filename-input');

    const findReplaceToggle = document.getElementById('find-replace-toggle');
    const findReplacePopup = document.getElementById('find-replace-popup');
    const closeFindReplaceBtn = document.getElementById('close-find-replace');
    const findInput = document.getElementById('find-input');
    const replaceInput = document.getElementById('replace-input');
    const replaceAllBtn = document.getElementById('replace-all-btn');
    const matchCaseBtn = document.getElementById('match-case-btn');
    const matchCountSpan = document.getElementById('match-count');
    const findReplaceMsg = document.getElementById('find-replace-msg');

    let isMatchCase = false;

    const editorScreenshotBtn = document.getElementById('editor-screenshot');
    const outputScreenshotBtn = document.getElementById('output-screenshot');
    const screenshotModal = document.getElementById('screenshot-modal');
    const screenshotPreviewImage = document.getElementById('screenshot-preview-image');
    const screenshotFilenameInput = document.getElementById('screenshot-filename');
    const downloadScreenshotBtn = document.getElementById('download-screenshot');
    const closeScreenshotBtn = document.getElementById('close-screenshot');
    const cancelScreenshotBtn = document.getElementById('cancel-screenshot');
    const loadingOverlay = document.getElementById('loading-overlay');
    const personalizeBtn = document.getElementById('personalize-btn');
    const personalizeModal = document.getElementById('personalize-modal');
    const personalNameInput = document.getElementById('personal-name-input');
    const applyPersonalizeBtn = document.getElementById('apply-personalize');
    const resetPersonalizeBtn = document.getElementById('reset-personalize');
    const cancelPersonalizeBtn = document.getElementById('cancel-personalize');
    const closePersonalizeBtn = document.getElementById('close-personalize');
    const personalizeTitle = document.getElementById('personalize-title');

    // Inline Input elements
    const inlineInputContainer = document.getElementById('inline-input-container');
    const inlineInputPrompt = document.getElementById('inline-input-prompt');
    const inlineUserInput = document.getElementById('inline-user-input');

    let currentLanguage = localStorage.getItem('bhasa_lang') || 'english';
    let isSideBySide = false;
    let isLightMode = localStorage.getItem('bhasa_theme') === 'light';

    function updateTheme() {
        if (isLightMode) {
            document.body.classList.add('light-theme');
            themeBtn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
            `; // Moon icon for switching TO Dark
            themeBtn.title = "Switch to Dark Mode";
        } else {
            document.body.classList.remove('light-theme');
            themeBtn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
            `; // Sun icon for switching TO Light
            themeBtn.title = "Switch to Light Mode";
        }
    }

    // Initial call
    updateTheme();

    themeBtn.addEventListener('click', () => {
        isLightMode = !isLightMode;
        localStorage.setItem('bhasa_theme', isLightMode ? 'light' : 'dark');
        updateTheme();
    });

    // Layout Toggle Logic
    layoutBtn.addEventListener('click', () => {
        isSideBySide = !isSideBySide;
        if (isSideBySide) {
            workspace.classList.add('layout-side-by-side');
            // Show icon for switching BACK to Top-Bottom (Horizontal Split)
            layoutBtn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                </svg>
            `;
            layoutBtn.title = "Switch to Top-Bottom View";
        } else {
            workspace.classList.remove('layout-side-by-side');
            // Show icon for switching TO Side-by-Side (Vertical Split)
            layoutBtn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="12" y1="3" x2="12" y2="21"></line>
                </svg>
            `;
            layoutBtn.title = "Switch to Side-by-Side View";
        }
    });

    function getEffectiveName() {
        const langInfo = config.getLanguage(currentLanguage);
        const customName = localStorage.getItem(`bhasa_name_${currentLanguage}`);
        return customName || langInfo.defaultName;
    }

    const highlighter = new SyntaxHighlighter();
    highlighter.setLanguage(currentLanguage, getEffectiveName());

    const updateKeyboard = initKeyboard(insertText, currentLanguage);

    personalizeBtn.addEventListener('click', () => {
        personalizeTitle.textContent = `Personalize for ${currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)}`;
        personalNameInput.value = getEffectiveName();
        personalizeModal.classList.remove('hidden');
        personalNameInput.focus();
        personalNameInput.select();
    });

    closePersonalizeBtn.addEventListener('click', () => personalizeModal.classList.add('hidden'));
    cancelPersonalizeBtn.addEventListener('click', () => personalizeModal.classList.add('hidden'));

    personalizeModal.addEventListener('click', (e) => {
        if (e.target === personalizeModal) personalizeModal.classList.add('hidden');
    });

    applyPersonalizeBtn.addEventListener('click', () => {
        const name = personalNameInput.value.trim();
        const langInfo = config.getLanguage(currentLanguage);

        if (name && name !== langInfo.defaultName) {
            localStorage.setItem(`bhasa_name_${currentLanguage}`, name);
        } else {
            localStorage.removeItem(`bhasa_name_${currentLanguage}`);
        }

        updateBhasaUI();
        personalizeModal.classList.add('hidden');
        logToConsole(`Personalization applied for ${currentLanguage}.`, 'success');
    });

    personalNameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') applyPersonalizeBtn.click();
        else if (e.key === 'Escape') personalizeModal.classList.add('hidden');
    });

    resetPersonalizeBtn.addEventListener('click', () => {
        const langInfo = config.getLanguage(currentLanguage);
        if (confirm(`Reset personalized name for ${currentLanguage} to default ("${langInfo.defaultName}")?`)) {
            localStorage.removeItem(`bhasa_name_${currentLanguage}`);
            updateBhasaUI();
            personalizeModal.classList.add('hidden');
            logToConsole(`Personalization reset for ${currentLanguage}.`);
        }
    });

    async function loadExampleFromURL() {
        const path = window.location.pathname;
        const parts = path.split('/').filter(p => p);

        if (parts.length >= 2) {
            const lang = parts[0];
            let program = parts.slice(1).join('/');

            const isValidLang = Array.from(languageSelect.options).some(opt => opt.value === lang);
            if (isValidLang) {
                if (program.endsWith('.bh')) {
                    program = program.replace(/\.bh$/, '');
                }

                currentLanguage = lang;
                localStorage.setItem('bhasa_lang', currentLanguage);
                languageSelect.value = currentLanguage;

                if (currentLanguage === 'arabic') {
                    document.documentElement.dir = 'rtl';
                } else {
                    document.documentElement.dir = 'ltr';
                }

                updateBhasaUI();

                try {
                    const response = await fetch(`/examples/${lang}/${program}.bh`);
                    const contentType = response.headers.get('content-type');

                    if (response.ok && (!contentType || !contentType.includes('text/html'))) {
                        const code = await response.text();
                        if (!code.trim().startsWith('<!DOCTYPE html>')) {
                            codeEditor.value = code;
                            updateLineNumbers();
                            updateHighlighting();
                            logToConsole(`Loaded example: ${program}.bh`, 'success');
                            return true;
                        }
                    }
                    throw new Error(`Example ${program}.bh not found.`);
                } catch (e) {
                    console.error(e);
                    logToConsole(e.message, 'error');
                }
            }
        }
        return false;
    }

    async function initIDE() {
        const loadedFromURL = await loadExampleFromURL();

        if (!loadedFromURL) {
            // Ensure dropdown matches stored state
            languageSelect.value = currentLanguage;

            // Set direction for initial language
            if (currentLanguage === 'arabic') {
                document.documentElement.dir = 'rtl';
            } else {
                document.documentElement.dir = 'ltr';
            }

            updateBhasaUI();

            if (Templates[currentLanguage]) {
                codeEditor.value = Templates[currentLanguage].replace(/{{name}}/g, getEffectiveName());
            }

            updateLineNumbers();
            updateHighlighting();
        }
    }

    function getCustomKeywords(lang) {
        const savedConfigStr = localStorage.getItem(`bhasa_config_${lang}`);
        if (savedConfigStr) {
            try {
                return JSON.parse(savedConfigStr);
            } catch (e) {
                console.error("Error parsing saved config", e);
            }
        }
        return {};
    }

    function updateBhasaUI() {
        const name = getEffectiveName();
        const customKeywords = getCustomKeywords(currentLanguage);

        // Update Icon State
        const isCustomName = localStorage.getItem(`bhasa_name_${currentLanguage}`) !== null;
        personalizeBtn.classList.toggle('active-status', isCustomName);

        // Update Keyboard
        updateKeyboard(currentLanguage, name, customKeywords);

        // Update Highlighter
        highlighter.setLanguage(currentLanguage, name);

        // Refresh editor
        updateLineNumbers();
        updateHighlighting();
    }

    // Phonetic Transliteration Logic
    const { transliterateWord } = require('./transliteration');
    let isPhoneticEnabled = false;
    const phoneticBtn = document.getElementById('phonetic-btn');
    const phoneticStatus = document.getElementById('phonetic-status');

    phoneticBtn.addEventListener('click', () => {
        isPhoneticEnabled = !isPhoneticEnabled;
        phoneticBtn.classList.toggle('active', isPhoneticEnabled);
        phoneticStatus.textContent = `Phonetic: ${isPhoneticEnabled ? 'ON' : 'OFF'}`;
        logToConsole(`Phonetic typing ${isPhoneticEnabled ? 'enabled' : 'disabled'}.`);
    });

    codeEditor.addEventListener('input', (e) => {
        if (!isPhoneticEnabled) return;

        // Trigger on space or enter
        const inputType = e.inputType;
        const lastChar = codeEditor.value[codeEditor.selectionStart - 1];

        if (lastChar === ' ' || lastChar === '\n') {
            const cursorPosition = codeEditor.selectionStart;
            const textBeforeCursor = codeEditor.value.substring(0, cursorPosition - 1);

            // Find the start of the last word
            const lastWordMatch = textBeforeCursor.match(/([a-zA-Z]+)$/);
            if (lastWordMatch) {
                const englishWord = lastWordMatch[1];
                const startPos = lastWordMatch.index;
                const endPos = cursorPosition - 1;

                const hindiWord = transliterateWord(englishWord, currentLanguage);

                if (hindiWord !== englishWord) {
                    // Replace the English word with the transliterated word
                    codeEditor.setRangeText(hindiWord, startPos, endPos, 'end');
                    updateLineNumbers();
                    updateHighlighting();
                }
            }
        }
    });

    // Initialize the IDE
    initIDE();

    languageSelect.addEventListener('change', (e) => {
        currentLanguage = e.target.value;
        localStorage.setItem('bhasa_lang', currentLanguage);

        updateBhasaUI();

        // Adjust direction for RTL languages
        if (currentLanguage === 'arabic') {
            document.documentElement.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
        }

        // Load template for the new language
        if (Templates[currentLanguage]) {
            codeEditor.value = Templates[currentLanguage].replace(/{{name}}/g, getEffectiveName());
            updateLineNumbers();
            updateHighlighting();
        }
    });

    clearBtn.addEventListener('click', () => {
        outputConsole.innerHTML = '';
    });

    // Copy, Save, Load Logic
    const copyBtn = document.getElementById('copy-code');
    const saveBtn = document.getElementById('save-code');
    const loadBtn = document.getElementById('load-code');
    const fileInput = document.getElementById('file-input');

    copyBtn.addEventListener('click', () => {
        const code = codeEditor.value;
        navigator.clipboard.writeText(code).then(() => {
            logToConsole('Code copied to clipboard!', 'success');
        }).catch(err => {
            console.error('Failed to copy: ', err);
            logToConsole('Failed to copy code.', 'error');
        });
    });

    saveBtn.addEventListener('click', () => {
        const now = new Date();
        const timestamp = now.getFullYear() +
            String(now.getMonth() + 1).padStart(2, '0') +
            String(now.getDate()).padStart(2, '0') +
            '_' +
            String(now.getHours()).padStart(2, '0') +
            String(now.getMinutes()).padStart(2, '0');

        filenameInput.value = `bhasa_${currentLanguage}_${timestamp}`;
        saveModal.classList.remove('hidden');
        filenameInput.focus();
        filenameInput.select();
    });

    function closeSaveModal() {
        saveModal.classList.add('hidden');
    }

    closeSaveBtn.addEventListener('click', closeSaveModal);
    cancelSaveBtn.addEventListener('click', closeSaveModal);
    saveModal.addEventListener('click', (e) => {
        if (e.target === saveModal) closeSaveModal();
    });

    confirmSaveBtn.addEventListener('click', () => {
        let filename = filenameInput.value.trim();
        if (!filename) {
            logToConsole('Please enter a filename.', 'error');
            return;
        }

        if (!filename.endsWith('.bh')) {
            filename += '.bh';
        }

        const code = codeEditor.value;
        const blob = new Blob([code], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        logToConsole(`Saved as ${filename}`, 'success');
        closeSaveModal();
    });

    filenameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            confirmSaveBtn.click();
        } else if (e.key === 'Escape') {
            closeSaveModal();
        }
    });

    loadBtn.addEventListener('click', () => {
        fileInput.click();
    });

    fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            codeEditor.value = e.target.result;
            updateLineNumbers();
            updateHighlighting();
            logToConsole(`Loaded ${file.name}`, 'success');
        };
        reader.readAsText(file);
        // Reset file input so the same file can be loaded again if needed
        fileInput.value = '';
    });

    // Full Screen Logic
    const editorFullScreenBtn = document.getElementById('editor-fullscreen');
    const outputFullScreenBtn = document.getElementById('output-fullscreen');
    const editorPane = document.querySelector('.editor-pane');
    const outputPane = document.querySelector('.output-pane');

    function toggleFullScreen(pane, btn) {
        const isFullScreen = pane.classList.toggle('pane-fullscreen');
        if (isFullScreen) {
            btn.innerHTML = `
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
                </svg>
            `;
            btn.title = "Exit Full Screen";
        } else {
            btn.innerHTML = `
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                </svg>
            `;
            btn.title = "Toggle Full Screen";
        }
    }

    editorFullScreenBtn.addEventListener('click', () => toggleFullScreen(editorPane, editorFullScreenBtn));
    outputFullScreenBtn.addEventListener('click', () => toggleFullScreen(outputPane, outputFullScreenBtn));

    // Screenshot Logic
    async function captureScreenshot(element, type) {
        logToConsole('Generating screenshot preview...');
        loadingOverlay.classList.remove('hidden');

        try {
            // Find scrollable content
            const scrollableChild = element.querySelector('.code-editor-wrapper') ||
                (element.classList.contains('console') ? element : null);

            if (!scrollableChild) throw new Error("Content not found");

            // Store original styles to restore later
            const originalStyles = {
                height: element.style.height,
                width: element.style.width,
                overflow: element.style.overflow,
                maxHeight: element.style.maxHeight,
                maxWidth: element.style.maxWidth
            };

            const originalChildStyles = scrollableChild !== element ? {
                height: scrollableChild.style.height,
                width: scrollableChild.style.width,
                overflow: scrollableChild.style.overflow
            } : null;

            // Calculate actual content dimensions
            // We need to temporarily "un-clip" things to get accurate scroll sizes
            element.style.overflow = 'visible';
            element.style.maxHeight = 'none';
            element.style.maxWidth = 'none';
            if (originalChildStyles) {
                scrollableChild.style.overflow = 'visible';
                scrollableChild.style.height = 'auto';
                scrollableChild.style.width = 'auto';
            }

            // For horizontal content, we need to check the highlighter specifically
            const highlighter = element.querySelector('.highlight-layer');
            const lineNumbers = element.querySelector('.line-numbers');

            let fullHeight = scrollableChild.scrollHeight;
            let fullWidth = scrollableChild.scrollWidth;

            if (highlighter) {
                // The actual text might be wider than the wrapper
                fullWidth = Math.max(fullWidth, highlighter.scrollWidth + (lineNumbers ? lineNumbers.offsetWidth : 0) + 40);
                fullHeight = Math.max(fullHeight, highlighter.scrollHeight);
            }

            // Set element to full size for capture
            element.style.height = fullHeight + 'px';
            element.style.width = fullWidth + 'px';

            const options = {
                scale: 1.5, // Optimized from 2
                backgroundColor: isLightMode ? '#ffffff' : '#0d1117',
                logging: false,
                useCORS: true,
                width: fullWidth,
                height: fullHeight,
                windowWidth: fullWidth + 200,
                windowHeight: fullHeight + 200,
                onclone: (clonedDoc) => {
                    // In the clone, hide the textarea
                    const clonedTextarea = clonedDoc.querySelector('#code-editor');
                    if (clonedTextarea) clonedTextarea.style.display = 'none';

                    // Ensure the highlighter and line numbers are visible and expanded
                    const clonedHighlighter = clonedDoc.querySelector('.highlight-layer');
                    if (clonedHighlighter) {
                        clonedHighlighter.style.position = 'relative';
                        clonedHighlighter.style.height = 'auto';
                        clonedHighlighter.style.width = 'max-content'; // Ensure it doesn't wrap
                        clonedHighlighter.style.minWidth = '100%';
                        clonedHighlighter.style.overflow = 'visible';
                    }

                    const clonedLineNumbers = clonedDoc.querySelector('.line-numbers');
                    if (clonedLineNumbers) {
                        clonedLineNumbers.style.height = 'auto';
                        clonedLineNumbers.style.overflow = 'visible';
                    }

                    const clonedWrapper = clonedDoc.querySelector('.code-editor-wrapper');
                    if (clonedWrapper) {
                        clonedWrapper.style.width = 'auto';
                        clonedWrapper.style.overflow = 'visible';
                    }
                }
            };

            const canvas = await html2canvas(element, options);

            // Restore original styles immediately
            Object.assign(element.style, originalStyles);
            if (originalChildStyles) {
                Object.assign(scrollableChild.style, originalChildStyles);
            }

            loadingOverlay.classList.add('hidden');

            const dataUrl = canvas.toDataURL('image/png');
            screenshotPreviewImage.src = dataUrl;

            const now = new Date();
            const timestamp = now.getFullYear() +
                String(now.getMonth() + 1).padStart(2, '0') +
                String(now.getDate()).padStart(2, '0') +
                '_' +
                String(now.getHours()).padStart(2, '0') +
                String(now.getMinutes()).padStart(2, '0');

            screenshotFilenameInput.value = `bhasa_${type}_${timestamp}.png`;
            screenshotModal.classList.remove('hidden');
            screenshotFilenameInput.focus();
            screenshotFilenameInput.select();

            logToConsole('Screenshot capture successful!', 'success');
        } catch (err) {
            loadingOverlay.classList.add('hidden');
            console.error('Screenshot failed:', err);
            logToConsole('Failed to capture screenshot.', 'error');
            // Try to restore styles anyway
            location.reload(); // Fallback if styles are totally broken
        }
    }

    editorScreenshotBtn.addEventListener('click', () => {
        const element = document.querySelector('.code-container');
        captureScreenshot(element, 'source_code');
    });

    outputScreenshotBtn.addEventListener('click', () => {
        const element = document.getElementById('output-console');
        captureScreenshot(element, 'output');
    });

    function closeScreenshotModal() {
        screenshotModal.classList.add('hidden');
        screenshotPreviewImage.src = '';
    }

    closeScreenshotBtn.addEventListener('click', closeScreenshotModal);
    cancelScreenshotBtn.addEventListener('click', closeScreenshotModal);

    screenshotModal.addEventListener('click', (e) => {
        if (e.target === screenshotModal) closeScreenshotModal();
    });

    downloadScreenshotBtn.addEventListener('click', () => {
        let filename = screenshotFilenameInput.value.trim();
        if (!filename) {
            logToConsole('Please enter a filename.', 'error');
            return;
        }
        if (!filename.toLowerCase().endsWith('.png')) {
            filename += '.png';
        }

        const link = document.createElement('a');
        link.download = filename;
        link.href = screenshotPreviewImage.src;
        link.click();
        closeScreenshotModal();
        logToConsole(`Screenshot saved as ${filename}`, 'success');
    });

    screenshotFilenameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            downloadScreenshotBtn.click();
        } else if (e.key === 'Escape') {
            closeScreenshotModal();
        }
    });

    // Line Numbers Logic
    function updateLineNumbers() {
        const lines = codeEditor.value.split('\n').length;
        // Add a trailing break to match the editor's scrollable area
        lineNumbers.innerHTML = Array(lines).fill(0).map((_, i) => i + 1).join('<br>') + '<br>';
    }

    codeEditor.addEventListener('input', () => {
        updateLineNumbers();
        updateHighlighting();
    });
    codeEditor.addEventListener('scroll', () => {
        lineNumbers.scrollTop = codeEditor.scrollTop;
        highlightLayer.scrollTop = codeEditor.scrollTop;
        highlightLayer.scrollLeft = codeEditor.scrollLeft;
    });

    // Find and Replace Logic
    findReplaceToggle.addEventListener('click', () => {
        findReplacePopup.classList.toggle('hidden');
        if (!findReplacePopup.classList.contains('hidden')) {
            findInput.focus();
            findInput.select();
            updateSearch();
        } else {
            updateHighlighting(); // Clear search highlights
        }
    });

    closeFindReplaceBtn.addEventListener('click', () => {
        findReplacePopup.classList.add('hidden');
        updateHighlighting(); // Clear search highlights
    });

    findInput.addEventListener('input', updateSearch);

    matchCaseBtn.addEventListener('click', () => {
        isMatchCase = !isMatchCase;
        matchCaseBtn.classList.toggle('active', isMatchCase);
        updateSearch();
    });

    function updateSearch() {
        const searchTerm = findInput.value;
        if (!searchTerm) {
            matchCountSpan.textContent = '0 matches';
            findReplaceMsg.textContent = '';
            updateHighlighting();
            return;
        }

        const code = codeEditor.value;
        const flags = isMatchCase ? 'g' : 'gi';
        const regex = new RegExp(highlighter.escapeRegex(searchTerm), flags);
        const matches = code.match(regex);
        const count = matches ? matches.length : 0;

        matchCountSpan.textContent = `${count} matches`;
        findReplaceMsg.textContent = '';
        updateHighlighting(searchTerm, isMatchCase);
    }

    replaceAllBtn.addEventListener('click', () => {
        const findText = findInput.value;
        const replaceText = replaceInput.value;

        if (!findText) return;

        const code = codeEditor.value;
        const flags = isMatchCase ? 'g' : 'gi';
        const regex = new RegExp(highlighter.escapeRegex(findText), flags);
        const matches = code.match(regex);
        const count = matches ? matches.length : 0;

        if (count > 0) {
            const newCode = code.replace(regex, replaceText);
            codeEditor.value = newCode;
            updateLineNumbers();
            updateHighlighting(findText, isMatchCase); // Keep highlighting for a moment
            setTimeout(() => updateHighlighting(), 1000); // Clear highlighting after 1s
            matchCountSpan.textContent = '0 matches';
            findReplaceMsg.textContent = `Replaced ${count} occurrences`;
            logToConsole(`Replaced ${count} occurrences of "${findText}" with "${replaceText}"`, 'success');
        } else {
            findReplaceMsg.textContent = 'No occurrences found';
        }
    });

    // Syntax Highlighting
    function updateHighlighting(searchTerm = '', caseSensitive = false) {
        const code = codeEditor.value;
        // Add a trailing newline character to match textarea behavior
        highlightLayer.innerHTML = highlighter.highlight(code, searchTerm, caseSensitive) + '\n';
    }

    function insertText(text) {
        const start = codeEditor.selectionStart;
        const end = codeEditor.selectionEnd;
        const val = codeEditor.value;
        codeEditor.value = val.substring(0, start) + text + val.substring(end);
        codeEditor.selectionStart = codeEditor.selectionEnd = start + text.length;
        codeEditor.focus();
        updateLineNumbers();
        updateHighlighting();
    }

    function logToConsole(message, type = 'log') {
        const line = document.createElement('div');
        line.textContent = String(message);
        line.className = 'console-line';
        if (type === 'error') line.classList.add('error');
        outputConsole.appendChild(line);
        outputConsole.scrollTop = outputConsole.scrollHeight;
    }

    // Settings Logic
    const settingsBtn = document.getElementById('settings-btn');
    const settingsModal = document.getElementById('settings-modal');
    const closeSettingsBtn = document.getElementById('close-settings');
    const saveSettingsBtn = document.getElementById('save-settings');
    const resetSettingsBtn = document.getElementById('reset-settings');
    const keywordsForm = document.getElementById('keywords-form');

    function toggleModal(show) {
        if (show) {
            settingsModal.classList.remove('hidden');
            renderKeywordsForm();
        } else {
            settingsModal.classList.add('hidden');
        }
    }

    settingsBtn.addEventListener('click', () => toggleModal(true));
    closeSettingsBtn.addEventListener('click', () => toggleModal(false));
    settingsModal.addEventListener('click', (e) => {
        if (e.target === settingsModal) toggleModal(false);
    });

    function getEffectiveConfig(lang) {
        return config.getResolvedKeywords(lang, getEffectiveName(), getCustomKeywords(lang));
    }

    function getRawConfig(lang) {
        const langInfo = config.getLanguage(lang);
        const custom = getCustomKeywords(lang);
        const baseKeywords = langInfo.keywords;

        const raw = {};
        for (const [key, value] of Object.entries(Keywords)) {
            raw[value] = custom[value] !== undefined ? custom[value] : baseKeywords[value];
        }
        return raw;
    }

    function renderKeywordsForm() {
        keywordsForm.innerHTML = '';
        const rawConfig = getRawConfig(currentLanguage);
        const baseKeywords = config.getLanguage(currentLanguage).keywords;

        // Reverse map Keywords from 'PROGRAM_START' -> 'Program Start' (Friendly Name)
        // Or just iterate over the Keywords keys
        for (const [key, value] of Object.entries(Keywords)) {
            // value is e.g., 'PROGRAM_START'
            // rawConfig[value] is e.g., 'hi {{name}}'

            const group = document.createElement('div');
            group.className = 'form-group';

            const label = document.createElement('label');
            label.textContent = value.replace(/_/g, ' '); // PROGRAM_START -> PROGRAM START
            label.style.textTransform = 'capitalize';

            const input = document.createElement('input');
            input.type = 'text';
            input.name = value;
            input.value = rawConfig[value] || '';
            input.placeholder = `Default: ${baseKeywords[value] || ''}`;

            group.appendChild(label);
            group.appendChild(input);
            keywordsForm.appendChild(group);
        }
    }

    saveSettingsBtn.addEventListener('click', () => {
        const formData = new FormData(document.querySelector('form#keywords-form')); // selecting by id might be safer if multiple forms exist
        const newConfig = {};

        // Need to reconstruct the object map. iterate over form inputs.
        const inputs = keywordsForm.querySelectorAll('input');
        inputs.forEach(input => {
            if (input.value.trim()) {
                newConfig[input.name] = input.value.trim();
            }
        });

        localStorage.setItem(`bhasa_config_${currentLanguage}`, JSON.stringify(newConfig));
        updateBhasaUI();
        toggleModal(false);
        logToConsole(`Configuration saved for ${currentLanguage}.`, 'success');
    });
    resetSettingsBtn.addEventListener('click', () => {
        if (confirm(`Reset keywords for ${currentLanguage} to defaults?`)) {
            localStorage.removeItem(`bhasa_config_${currentLanguage}`);
            updateBhasaUI();
            renderKeywordsForm(); // Refresh form
            logToConsole(`Configuration reset for ${currentLanguage}.`);
        }
    });

    runBtn.addEventListener('click', async () => {
        outputConsole.innerHTML = ''; // Auto clear on run? Maybe.

        // Ensure inline input container is preserved but hidden
        outputConsole.appendChild(inlineInputContainer);
        inlineInputContainer.classList.add('hidden');

        const source = codeEditor.value;
        if (!source.trim()) return;

        let inputResolve = null;

        function closeInlineInput() {
            inlineInputContainer.classList.add('hidden');
            inlineUserInput.value = '';
        }

        function requestInput(promptText) {
            return new Promise((resolve) => {
                inputResolve = resolve;

                // If there's a prompt, ensure the trailing space is formatted well
                const formattedPrompt = promptText ? promptText.trim() + " " : "";
                inlineInputPrompt.textContent = formattedPrompt;
                inlineUserInput.value = '';

                // Move the input container to the bottom of the console
                outputConsole.appendChild(inlineInputContainer);
                inlineInputContainer.classList.remove('hidden');

                // Scroll to the bottom and focus
                outputConsole.scrollTop = outputConsole.scrollHeight;
                inlineUserInput.focus();
            });
        }

        const handleInputSubmit = (e) => {
            if (e.key === 'Enter' && inputResolve) {
                const val = inlineUserInput.value;

                // Print the prompt and the user's input to the console history
                // Doing this makes it look like a real terminal
                const consoleOutput = (inlineInputPrompt.textContent + val).trim();
                if (consoleOutput) {
                    logToConsole(consoleOutput);
                }

                inputResolve(val);
                inputResolve = null;
                closeInlineInput();
            }
        };

        // We only want to attach this once or ensure it doesn't double-fire
        inlineUserInput.removeEventListener('keydown', inlineUserInput._submitHandler);
        inlineUserInput._submitHandler = handleInputSubmit;
        inlineUserInput.addEventListener('keydown', handleInputSubmit);

        try {
            const langConfig = getEffectiveConfig(currentLanguage);

            // 1. Lexer
            const lexer = new Lexer(source, langConfig);
            const tokens = lexer.tokenize();

            // 2. Parser
            const parser = new Parser(tokens);
            const ast = parser.parse();

            // 3. Interpreter
            const interpreter = new Interpreter(logToConsole, requestInput, { loopLimit: __LOOP_LIMIT__ });
            runBtn.disabled = true;
            runBtn.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" />
                </svg>
                Running...
            `;
            await interpreter.execute(ast);

        } catch (err) {
            console.error(err);
            logToConsole(err.message, 'error');
        } finally {
            runBtn.disabled = false;
            runBtn.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                </svg>
                Run
            `;
            closeInlineInput();
        }
    });


});
